import { useTranslation } from "react-i18next";

import type { HintSeverity } from "@/lib/hints";

// Display-fertige Zeile der Alignment-Übersicht (Labels löst der Aufrufer auf,
// weil Wizard und Editor unterschiedliche Key-Typen haben).
export interface OverviewRow {
  iloLabel: string;
  iloText: string;
  tlas: { label: string; severity: HintSeverity | null }[];
  ats: { label: string; severity: HintSeverity | null }[];
}

const SEVERITY_CHIP: Record<HintSeverity, string> = {
  ok: "bg-verdict-green-bg text-verdict-green border-verdict-green/40",
  tension: "bg-verdict-amber-bg text-verdict-amber border-verdict-amber/40",
  misalignment: "bg-verdict-red-bg text-verdict-red border-verdict-red/40",
};

const UNKNOWN_CHIP = "bg-ca-card text-ca-surface-muted border-ca-surface-border";

// Bipartite Alignment-Visualisierung: pro Lernziel eine Zeile mit Chips der
// verlinkten Methoden („bereitet vor") und Assessments („prüft"). Chip-Farbe
// = Bloom-Distanz-Severity des Paars (ok/tension/misalignment, null =
// nicht bewertbar). Lernziele ohne Verknüpfung sind visuell markiert.
export default function AlignmentOverview({ rows }: { rows: OverviewRow[] }) {
  const { t } = useTranslation();
  if (rows.length === 0) return null;

  return (
    <div className="p-6 bg-ca-card rounded-2xl border border-ca-surface-border">
      <h3 className="text-xs font-black uppercase tracking-widest text-ca-surface-muted mb-4">
        {t("hints.overview.title")}
      </h3>
      <div className="space-y-4">
        {rows.map((row, i) => {
          const unlinked = row.tlas.length === 0 && row.ats.length === 0;
          return (
            <div
              key={i}
              className={`rounded-xl p-4 border ${
                unlinked
                  ? "border-dashed border-verdict-red/50 bg-verdict-red-bg/40"
                  : "border-ca-surface-border"
              }`}
            >
              <p className="text-sm text-ca-surface-ink mb-2">
                <span className="font-black text-ca-surface-ink mr-2">{row.iloLabel}</span>
                {row.iloText}
                {unlinked && (
                  <span className="ml-2 text-xs font-semibold text-verdict-red">
                    — {t("hints.overview.unlinked")}
                  </span>
                )}
              </p>
              {!unlinked && (
                <div className="flex flex-wrap items-center gap-2">
                  {row.tlas.length > 0 && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-ca-surface-muted">
                      {t("hints.overview.prepares")}:
                    </span>
                  )}
                  {row.tlas.map((chip, j) => (
                    <span
                      key={`tla-${j}`}
                      className={`px-2 py-0.5 rounded-md border text-xs font-semibold ${
                        chip.severity ? SEVERITY_CHIP[chip.severity] : UNKNOWN_CHIP
                      }`}
                    >
                      {chip.label}
                    </span>
                  ))}
                  {row.ats.length > 0 && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-ca-surface-muted">
                      {t("hints.overview.assesses")}:
                    </span>
                  )}
                  {row.ats.map((chip, j) => (
                    <span
                      key={`at-${j}`}
                      className={`px-2 py-0.5 rounded-md border text-xs font-semibold ${
                        chip.severity ? SEVERITY_CHIP[chip.severity] : UNKNOWN_CHIP
                      }`}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
